import React from "react";
import { createInertiaApp } from "@inertiajs/react";
import { createRoot } from "react-dom/client";
import Honeybadger from "@honeybadger-io/js";
import { HoneybadgerErrorBoundary } from "@honeybadger-io/react";
import CustomErrorBoundaryComponent from "./custom_error_boundary";
import "../utils/axios_setup";
import { initMobiLoudPush } from "../mobiloud";
import {
  initCapacitorPush,
  registerBackButton,
  initNativeUi,
  initKeyboard,
  initOfflineWatch,
  registerResumeRefresh,
  registerCableReconnect,
  registerPullToRefresh,
  registerDeepLinks,
} from "../capacitor";
import { initializeMultiAccount } from "../utils/multi_account_init";
import { initAppNotificationUrlParamHook } from "../utils/app_notification_url_param";

document.addEventListener("DOMContentLoaded", () => {
  // Initialize native push notifications. Each no-ops outside its own shell, so
  // both can be called during the MobiLoud → Capacitor cutover.
  initMobiLoudPush();
  initCapacitorPush();
  // Route Android's back button to in-app history (no-op off Android/Capacitor).
  registerBackButton();
  // Native keyboard UX: keyboard-open class + keep chat pinned to newest
  // message (no-op off the Capacitor shell).
  initKeyboard();
  // Show a native offline banner when connectivity drops (no-op off-shell).
  initOfflineWatch();
  // Refresh the web app when reopened after a long background (no-op off-shell).
  registerResumeRefresh();
  // Reopen the ActionCable socket on foreground so chat/badges resume live
  // updates after the WebView suspended it (no-op off the Capacitor shell).
  registerCableReconnect();
  // Pull-to-refresh the current page from the top of the main scroller
  // (no-op off the Capacitor shell).
  registerPullToRefresh();
  // Route incoming Universal/App Links to the in-app router (no-op off-shell).
  registerDeepLinks();

  // Initialize multi-account support (adds current session to localStorage if enabled)
  initializeMultiAccount();

  // Suppress benign ResizeObserver errors from browser console
  // These occur frequently with Material UI and don't indicate real problems
  window.addEventListener("error", (event) => {
    if (event.message?.includes("ResizeObserver loop")) {
      event.stopImmediatePropagation();
    }
  });

  const honeybadgerEnvironment = window.aws_env?.trim() || window.environment;

  const honeybadger = Honeybadger.configure({
    apiKey: "bcc95794",
    environment: honeybadgerEnvironment,
    // Uncomment to enable reporting in development
    // reportData: true,
  });

  if (window._tkcCurrentUserId) {
    honeybadger.setContext({
      user_id: window._tkcCurrentUserId,
      user_email: window.bloc_user_email,
    });
  }

  const ignorePatterns = [
    /This video will be available for viewing shortly/i,
    /ResizeObserver loop limit exceeded/i,
    /ResizeObserver loop completed with undelivered notifications/i,
  ];

  Honeybadger.beforeNotify(function (notice) {
    if (ignorePatterns.some((p) => p.test(notice.message))) {
      return false;
    }
  });

  const el = document.querySelector("div#app");

  if (el) {
    createInertiaApp({
      resolve: async (name) => {
        if (!name) {
          throw new Error(
            `Inertia resolve: page name is ${JSON.stringify(name)} — cannot import dynamic module path`,
          );
        }
        const module = await import(`../pages/${name}`);
        return module.default;
      },
      setup({ el, App, props }) {
        createRoot(el).render(
          <HoneybadgerErrorBoundary
            honeybadger={honeybadger}
            ErrorComponent={CustomErrorBoundaryComponent}
          >
            <App {...props} />
          </HoneybadgerErrorBoundary>,
        );
        // After the first page mounts: hide the launch splash on real content
        // and sync the native status bar to the theme (no-op off the Capacitor
        // shell). Done here (not on DOMContentLoaded) so the splash hides over
        // painted app content, avoiding a white flash.
        initNativeUi();
      },
    });

    // notifications_v2 push-tap → mark-read sync (PR-N1). Reads
    // ?app_notification_id=N from the URL on full page load AND on every
    // Inertia client-side navigation, fires PATCH mark_as_read, strips the
    // param so a back-nav doesn't re-fire. Half B (push dismiss) ships in
    // a later PR.
    initAppNotificationUrlParamHook();
  }
});
